import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ApiError, IconButton, formatDate, formatRange, useSnackbar } from '@jupi/ui'
import type { InstructorAvailability } from '@jupi/shared'
import { useAvailabilityMutations } from '../../api/lessons'
import {
  DAY_PRESETS,
  TIME_PRESETS,
  blank,
  draftFault,
  isSingleDay,
  isoWeekday,
  requestsFor,
  weekdayName,
  type AvailabilityDraft,
} from './availabilityDraft'

export type { AvailabilityDraft }

// What the instructor has offered: one-off windows and weekly rules, each
// removable, and a dialog to add more.
export function AvailabilityPanel({
  instructorUserId,
  availability,
  draft,
  onDraftHandled,
}: {
  instructorUserId: string
  availability: InstructorAvailability | undefined
  // A span picked on the calendar opens the dialog filled in with it.
  draft: AvailabilityDraft | null
  onDraftHandled: () => void
}) {
  const { t, i18n } = useTranslation()
  const phone = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const { showSnackbar } = useSnackbar()
  const { add, remove } = useAvailabilityMutations(instructorUserId)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState<AvailabilityDraft>(blank)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!draft) return
    setForm(draft)
    setError(null)
    setOpen(true)
    onDraftHandled()
  }, [draft, onDraftHandled])

  const single = isSingleDay(form)

  const fail = (err: unknown) => {
    const message = err instanceof ApiError ? err.message : t('common.error')
    showSnackbar(message, { severity: 'error' })
    return message
  }

  const save = async () => {
    setError(null)
    const fault = draftFault(form)
    if (fault) return setError(t(`lessons.${fault}`))
    try {
      await add.mutateAsync(requestsFor(form))
      setOpen(false)
      showSnackbar(t('lessons.availabilityAdded'))
    } catch (err) {
      setError(fail(err))
    }
  }

  const toggleDay = (day: number) =>
    setForm((f) => ({
      ...f,
      weekdays: [1, 2, 3, 4, 5, 6, 7].filter((d) => (d === day ? !f.weekdays.includes(d) : f.weekdays.includes(d))),
    }))

  const withdraw = async (target: { kind: 'window' | 'rule'; id: string }) => {
    try {
      await remove.mutateAsync(target)
    } catch (err) {
      const lessons = err instanceof ApiError && err.status === 409
      showSnackbar(lessons ? t('lessons.availabilityHasLessons') : fail(err), {
        severity: 'error',
      })
    }
  }

  const windows = availability?.windows ?? []
  const rules = availability?.rules ?? []
  const openBooking = availability?.openBooking ?? false

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6" component="h2">
          {t('lessons.availability')}
        </Typography>
      </Stack>
      {openBooking ? (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('lessons.openBookingOn')}
        </Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('lessons.availabilityHelp')} {t('lessons.calendarHelp')}
        </Typography>
      )}
      {!openBooking && windows.length === 0 && rules.length === 0 ? (
        <Alert severity="info">{t('lessons.noAvailability')}</Alert>
      ) : null}
      <List dense disablePadding>
        {rules.map((r) => (
          <ListItem
            key={r.ruleId}
            disableGutters
            secondaryAction={
              <IconButton
                size="small"
                aria-label={t('common.delete')}
                onClick={() => withdraw({ kind: 'rule', id: r.ruleId })}
              >
                <Icon icon="mdi:delete-outline" />
              </IconButton>
            }
          >
            <ListItemText
              primary={t('lessons.everyWeekday', {
                weekday: weekdayName(r.weekday, i18n.language),
                from: r.startTime.slice(0, 5),
                to: r.endTime.slice(0, 5),
              })}
              secondary={
                r.validTo
                  ? t('lessons.validBetween', {
                      from: formatDate(`${r.validFrom}T12:00:00Z`),
                      to: formatDate(`${r.validTo}T12:00:00Z`),
                    })
                  : t('lessons.validFrom', {
                      from: formatDate(`${r.validFrom}T12:00:00Z`),
                    })
              }
            />
          </ListItem>
        ))}
        {windows.map((w) => (
          <ListItem
            key={w.availabilityId}
            disableGutters
            secondaryAction={
              <IconButton
                size="small"
                aria-label={t('common.delete')}
                onClick={() => withdraw({ kind: 'window', id: w.availabilityId })}
              >
                <Icon icon="mdi:delete-outline" />
              </IconButton>
            }
          >
            <ListItemText
              primary={`${formatDate(w.startsAt)} ${formatRange(w.startsAt, w.endsAt)}`}
              secondary={w.note}
            />
          </ListItem>
        ))}
      </List>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" fullScreen={phone}>
        <DialogTitle>{t('lessons.addAvailability')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                label={t('lessons.from')}
                value={form.from}
                onChange={(e) => setForm({ ...form, from: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                type="date"
                label={t('lessons.until')}
                value={form.until}
                onChange={(e) => setForm({ ...form, until: e.target.value })}
                helperText={t('lessons.untilHelp')}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
            </Stack>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {single
                  ? t('lessons.singleDay', {
                      weekday: weekdayName(isoWeekday(form.from), i18n.language),
                    })
                  : t('lessons.days')}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {DAY_PRESETS.map((p) => (
                  <Chip
                    key={p.key}
                    size="small"
                    variant="outlined"
                    label={t(`lessons.${p.key}`)}
                    disabled={single}
                    onClick={() => setForm({ ...form, weekdays: [...p.days] })}
                  />
                ))}
              </Stack>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <Chip
                    key={d}
                    label={weekdayName(d, i18n.language, 'short')}
                    color={form.weekdays.includes(d) ? 'primary' : 'default'}
                    variant={form.weekdays.includes(d) ? 'filled' : 'outlined'}
                    disabled={single}
                    onClick={() => toggleDay(d)}
                  />
                ))}
              </Stack>
            </Box>
            <Stack direction="row" spacing={2}>
              <TextField
                type="time"
                label={t('lessons.startTime')}
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <TextField
                type="time"
                label={t('lessons.endTime')}
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
            </Stack>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {TIME_PRESETS.map((p) => (
                <Chip
                  key={p.key}
                  size="small"
                  variant="outlined"
                  label={`${t(`lessons.${p.key}`)} ${p.from}–${p.to}`}
                  onClick={() => setForm({ ...form, startTime: p.from, endTime: p.to })}
                />
              ))}
            </Stack>
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={save} disabled={add.isPending}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Open booking: students may book at any time the instructor is not already
// booked, with no availability to publish. Shown at the top of the page.
export function OpenBookingSwitch({
  instructorUserId,
  openBooking,
}: {
  instructorUserId: string
  openBooking: boolean
}) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { setOpen } = useAvailabilityMutations(instructorUserId)
  return (
    <FormControlLabel
      sx={{ alignItems: 'flex-start', mb: 2, mx: 0 }}
      control={
        <Switch
          checked={openBooking}
          disabled={setOpen.isPending}
          onChange={(e) =>
            setOpen.mutate(e.target.checked, {
              onError: (err) =>
                showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' }),
            })
          }
        />
      }
      label={
        <Box sx={{ pt: 0.75 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('lessons.openBooking')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('lessons.openBookingHelp')}
          </Typography>
        </Box>
      }
    />
  )
}
